import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  PaginationItem,
  Pagination,
  useMediaQuery
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import { formatDatee } from "../../components/reusableComponent/DateFormat";
import {
  getBadgeClasses,
  getIconClasses,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import ReactModal from "react-modal";
import { useUnassignedQuery } from "../../Services/activity/UnassignedTickets";
import UnassignedFilterModal from "./UnassignedFilterModal";
import { toast } from "react-toastify";
import BulkAssigneeModal from "../../components/ticket/BulkAssigneeModal";
import AssigneeBulkTeam from "../../components/email/inboxEmailModal/AssigneeBulkTeam";
import { ACTIVITY_PRIMARY, activityToolbarOutlinedSx } from "./activityUiTokens";

const customFilterModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "50px",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

const customModalStyles1 = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "450px",
  },
};

const SCROLL_POSITION_KEY = "unassignedTicketsScrollPosition";
const LAST_VIEWED_TASK_KEY = "lastViewedUnassigned";

const parseAddressedTo = (addressedTo: string | null | undefined): string => {
  if (!addressedTo) return "";

  try {
    if (typeof addressedTo === "string") {
      const cleaned = addressedTo
        .replace(/[\[\]']/g, "")
        .replace(/"/g, "")
        .trim();

      if (cleaned) {
        const emails = cleaned
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
        return emails.join(", ");
      }
    }
    return "";
  } catch (error) {
    console.error("Error parsing addressed_to:", error);
    return addressedTo?.toString() || "";
  }
};

const usePaginationState = (taskSource: "email" | "ticket") => {
  const storageKey = `unassigned_${taskSource}_pagination`;

  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = sessionStorage.getItem(`${storageKey}_pageSize`);
    return saved ? parseInt(saved) : 10;
  });

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = sessionStorage.getItem(`${storageKey}_currentPage`);
    return saved ? parseInt(saved) : 1;
  });

  const updatePageSize = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    sessionStorage.setItem(`${storageKey}_pageSize`, newSize.toString());
    sessionStorage.setItem(`${storageKey}_currentPage`, "1");
  };

  const updateCurrentPage = (newPage: number) => {
    setCurrentPage(newPage);
    sessionStorage.setItem(`${storageKey}_currentPage`, newPage.toString());
  };

  const refreshState = () => {
    const savedPageSize = sessionStorage.getItem(`${storageKey}_pageSize`);
    const savedCurrentPage = sessionStorage.getItem(`${storageKey}_currentPage`);

    setPageSize(savedPageSize ? parseInt(savedPageSize) : 10);
    setCurrentPage(savedCurrentPage ? parseInt(savedCurrentPage) : 1);
  };

  return {
    pageSize,
    currentPage,
    updatePageSize,
    updateCurrentPage,
    refreshState,
  };
};

const UnAssignedTickets = ({
  task_source,
}: {
  task_source: "email" | "ticket";
}) => {
  const isSmallScreen = useMediaQuery("(max-width:575px)");
  const isPageScreen = useMediaQuery("(max-width:510px)");
  const isExtraSmallScreen = useMediaQuery("(max-width:450px)");
  const [loading, setLoading] = useState<boolean>(false);
  const [isFilterModalOpen, setFilterIsModalOpen] = useState(false);
  const [isFilterOn, setIsFilterOn] = useState<boolean>(false);
  const [isBulkAssigneeModalOpen, setIsBulkAssigneeModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  const selectedTeam = localStorage.getItem("circleone-team");
  const {
    pageSize,
    currentPage,
    updatePageSize,
    updateCurrentPage,
    refreshState,
  } = usePaginationState(task_source);

  // Refs for scroll position management (same pattern as AccountCards)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    refreshState();
  }, [task_source]);

  const pageSizeOptions = [10, 25, 50, 100];

  const [selectedUnassignedFilters, setSelectedUnassignedFilters] =
    useState<any>({
      id: "",
      email_id: "",
      created_date: "",
      channel: "",
      disposition: "",
    });

  const [selectedUnassignedFilters1, setSelectedUnassignedFilters1] =
    useState<any>(() => {
      const sessionData = sessionStorage.getItem(
        `selectedUnassignedFilters_${task_source}`
      );
      return sessionData
        ? JSON.parse(sessionData)
        : {
            id: "",
            email_id: "",
            created_date: "",
            channel: "",
            disposition: "",
          };
    });

  useEffect(() => {
    const sessionData = sessionStorage.getItem(
      `selectedUnassignedFilters_${task_source}`
    );
    if (sessionData) {
      setSelectedUnassignedFilters1(JSON.parse(sessionData));
    } else {
      setSelectedUnassignedFilters1({
        id: "",
        email_id: "",
        created_date: "",
        channel: "",
        disposition: "",
      });
    }
  }, [task_source]);

  const { data: UnassignedTicketsData, refetch } = useUnassignedQuery({
    currentPage,
    selectedUnassignedFilters1,
    isFilterOn,
    task_source,
    pageSize,
    selectedTeam,
  });

  const rows =
    UnassignedTicketsData?.results?.map((ticket: any) => ({
      id: ticket?.id,
      created: ticket?.created,
      disposition_name: ticket?.disposition_name,
      current_assignee: ticket?.current_assignee || "Unassigned",
      email_id: ticket?.email_id,
      channel: ticket?.channel,
      task_source: ticket?.task_source,
      addressed_to: parseAddressedTo(ticket?.addressed_to),
    })) || [];

  const totalPages = Math.ceil(UnassignedTicketsData?.count / pageSize);

  useEffect(() => {
    if (UnassignedTicketsData) {
      setLoading(false);
    }
  }, [UnassignedTicketsData]);

  useEffect(() => {
    if (
      selectedUnassignedFilters1 &&
      Object.values(selectedUnassignedFilters1).some((value) => value !== "")
    ) {
      setIsFilterOn(true);
    } else {
      setIsFilterOn(false);
    }
  }, [selectedUnassignedFilters1]);

  useEffect(() => {
    setSelectAll(false);
    setSelectedTickets([]);
  }, [UnassignedTicketsData]);

  useEffect(() => {
    sessionStorage.setItem(
      `selectedUnassignedFilters_${task_source}`,
      JSON.stringify(selectedUnassignedFilters1)
    );
  }, [selectedUnassignedFilters1, task_source]);

   // Store scroll position before page unload (e.g. refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const scrollableContainer = scrollContainerRef.current;
      if (scrollableContainer) {
        sessionStorage.setItem(
          SCROLL_POSITION_KEY,
          String(scrollableContainer.scrollTop)
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const openFilterModal = () => {
    setFilterIsModalOpen(true);
  };

  const closeFilterModal = () => {
    setFilterIsModalOpen(false);
  };

  // Store scroll position when navigating away (use element that actually scrolls)
  const storeScrollPosition = (taskId: string) => {
    const scrollableContainer = scrollContainerRef.current;
    if (scrollableContainer) {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        String(scrollableContainer.scrollTop)
      );
    }
    sessionStorage.setItem(LAST_VIEWED_TASK_KEY, taskId);
  };

  // Restore scroll position when returning (retry until content is laid out)
  const restoreScrollPosition = (): (() => void) => {
    const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (!savedScrollPosition) return () => {};

    const targetScrollTop = parseInt(savedScrollPosition, 10);
    if (Number.isNaN(targetScrollTop) || targetScrollTop < 0) return () => {};

    const applyScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = targetScrollTop;
    };

    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(applyScroll, d));
    requestAnimationFrame(() => {
      requestAnimationFrame(applyScroll);
      clearScrollPosition();
    });
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
    sessionStorage.removeItem(LAST_VIEWED_TASK_KEY);
  };

  const handleSubmit = (filterValues) => {
    const { task_source: _removed, ...rest } = filterValues;
    setSelectedUnassignedFilters1(rest);
    updateCurrentPage(1);
  };

  // Restore scroll position when list is visible (not loading, has results)
  useEffect(() => {
    const canRestore =
      !loading &&
      rows.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function") {
          scrollRestoreCleanupRef.current = cleanup;
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        if (scrollRestoreCleanupRef.current) {
          scrollRestoreCleanupRef.current();
          scrollRestoreCleanupRef.current = null;
        }
      };
    }
  }, [loading, rows]);

  const openBulkModal = () => {
    if (selectedTickets.length === 0) {
      return toast.error(
        `Please select at least one ${
          task_source === "email" ? "email" : "ticket"
        }.`
      );
    } else {
      setIsBulkAssigneeModalOpen(true);
    }
  };

  const closeBulkModal = () => {
    setSelectedTickets([]);
    setIsBulkAssigneeModalOpen(false);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);

    if (isChecked) {
      const allTicketIds = rows.map((row) => row.id.toString());
      setSelectedTickets(allTicketIds);
    } else {
      setSelectedTickets([]);
    }
  };

  const handleTicketSelect = (ticketId: string) => {
    setSelectedTickets((prev) => {
      const newSelected = prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId];

      setSelectAll(newSelected.length === rows.length && rows.length > 0);

      return newSelected;
    });
  };

  const handleRowClick = (event: React.MouseEvent, row: any) => {
    if ((event.target as HTMLElement).closest(".MuiCheckbox-root")) {
      return;
    }
    storeScrollPosition(String(row.id));

    const url =
      row.task_source === "email"
        ? `/email/inbox/${row.id}`
        : row.task_source === "ticket"
        ? `/ticket/information/${row.id}`
        : null;

    if (url) {
      window.location.href = url;
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    updateCurrentPage(newPage);
    clearScrollPosition();
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    updatePageSize(newSize);
    clearScrollPosition();
  };

  useEffect(() => {
    setSelectedTickets([]);
    setSelectAll(false);

    console.log(`Component mounted/updated for ${task_source}`);
    console.log(`Current pageSize: ${pageSize}, currentPage: ${currentPage}`);
  }, [task_source, pageSize, currentPage]);

  // Common style for sticky header cells
  const stickyCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 10,
    fontWeight: 600,
    fontSize: isSmallScreen ? "13px" : "15px",
    padding: isSmallScreen ? "8px 4px" : "16px",
    minWidth: isSmallScreen ? "80px" : "auto",
  };

  const stickyCheckboxCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 11, // Higher zIndex for checkbox cell
    padding: isSmallScreen ? "8px 4px" : "16px",
  };

  return (
    <>
      <div
        className="card-toolbar flex-row-fluid justify-content-between d-flex mb-5"
        style={{
          marginLeft: isSmallScreen ? "10px" : "30px",
          gap: isSmallScreen ? "10px" : "0"
        }}
      >
        <div className="d-flex align-items-center">
          <h2 style={{
            marginLeft: "-28px",
            fontSize: isExtraSmallScreen ? "13px" : isSmallScreen ? "16px" : ""
          }}>
            Unassigned {task_source === "ticket" ? "tickets" : "emails"}
          </h2>
        </div>

        <div className="d-flex gap-3 align-items-center">
          <a
            className="btn btn-sm btn-icon btn-light btn-active-light-primary mx-3"
            data-bs-toggle="tooltip"
            data-bs-dismiss="click"
            data-bs-placement="top"
            aria-label="Assignee"
            data-bs-original-title="Assignee"
            data-kt-initialized={1}
            title="Assignee"
            onClick={openBulkModal}
          >
            <i className="bi bi-person-fill fs-2" />
          </a>

          <Button
            variant="outlined"
            className="fw-bold"
            onClick={openFilterModal}
            sx={{
              ...activityToolbarOutlinedSx,
              height: isSmallScreen ? 32 : 42,
              width: isExtraSmallScreen ? 80 : isSmallScreen ? 100 : 140,
              gap: "5px",
              alignItems: "center",
              display: "flex",
              fontSize: isSmallScreen ? "13px" : "14px",
            }}
          >
            <i className="bi bi-filter fs-2" style={{ color: ACTIVITY_PRIMARY }} />
            <span
              className="ms-auto me-2"
              style={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: isSmallScreen ? "11px" : "14px",
                color: ACTIVITY_PRIMARY,
              }}
            >
              {isSmallScreen ? "Filter" : "Search filter"}
            </span>
          </Button>
        </div>
      </div>

      <ReactModal
        isOpen={isBulkAssigneeModalOpen}
        onRequestClose={closeBulkModal}
        style={customModalStyles1}
        contentLabel="Bulk Assignee"
        shouldCloseOnOverlayClick={false}
      >
        {task_source === "email" ? (
          <AssigneeBulkTeam
            closeModal={closeBulkModal}
            setectedEmail={selectedTickets}
            refetchData={refetch}
            setSelectedRows={setSelectedTickets}
          />
        ) : (
          <BulkAssigneeModal
            closeBulkModal={closeBulkModal}
            selectedTickets={selectedTickets}
            refetch={refetch}
          />
        )}
      </ReactModal>

      <ReactModal
        isOpen={isFilterModalOpen}
        onRequestClose={closeFilterModal}
        style={customFilterModalStyles}
        contentLabel="Unassigned Ticket Filter Modal"
        shouldCloseOnOverlayClick={false}
      >
        <UnassignedFilterModal
          closeFilterModal={closeFilterModal}
          selectedFilterAndValue2={selectedUnassignedFilters}
          setSelectedFilterAndValue2={setSelectedUnassignedFilters}
          handleSubmit={handleSubmit}
          taskSource={task_source}
        />
      </ReactModal>

      <TableContainer
        component={Paper}
        ref={scrollContainerRef}
        style={{ maxHeight: 620, overflowY: "auto" }}
      >
        <Table aria-label="unassigned tickets table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={stickyCheckboxCellStyle}>
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={
                    selectedTickets.length > 0 &&
                    selectedTickets.length < rows.length
                  }
                  color="primary"
                />
              </TableCell>
              {/* Conditionally render Ref id column only for tickets */}
              {task_source !== "email" && (
                <TableCell sx={stickyCellStyle}>Ref id</TableCell>
              )}
              <TableCell sx={stickyCellStyle}>Created date</TableCell>
              <TableCell sx={stickyCellStyle}>Disposition</TableCell>
              <TableCell sx={stickyCellStyle}>Current assignee</TableCell>
              <TableCell sx={stickyCellStyle}>Email id</TableCell>
              {task_source !== "ticket" && (
                <TableCell sx={stickyCellStyle}>Addressed to</TableCell>
              )}
              {task_source !== "email" && (
                <TableCell sx={stickyCellStyle}>Channel</TableCell>
              )}
              <TableCell sx={stickyCellStyle}>Task source</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} style={{ textAlign: "center" }}>
                  <LoadingSpinner />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    No data available
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: any) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={(event) => handleRowClick(event, row)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTickets.includes(row.id.toString())}
                      onChange={() => handleTicketSelect(row.id.toString())}
                      color="primary"
                    />
                  </TableCell>
                  {/* Conditionally render Ref id cell only for tickets */}
                  {task_source !== "email" && <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>{row.id}</TableCell>}
                  <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>{row.created}</TableCell>
                  <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>
                    {capitalizeFirstLetter(row.disposition_name)}
                  </TableCell>
                  <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>
                    {capitalizeFirstLetter(row.current_assignee)}
                  </TableCell>
                  <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>{row.email_id}</TableCell>
                  {task_source !== "ticket" && (
                    <TableCell sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>{row.addressed_to || "N/A"}</TableCell>
                  )}
                  {task_source !== "email" && (
                    <TableCell
                      title={row.channel}
                      style={{ paddingLeft: "28px" }}
                      sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}
                    >
                      <div
                        className={`${getBadgeClasses(row.channel)}`}
                        style={{
                          width: "35px",
                          height: "35px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                        }}
                      >
                        <i className={getIconClasses(row.channel)}></i>
                      </div>
                    </TableCell>
                  )}
                  <TableCell sx={{ width: "100px", minWidth: "100px", fontSize: isSmallScreen ? "12px" : "14px" }}>
                    <span
                      className={`badge ${row.task_source === "email"
                          ? "badge-primary"
                          : "badge-success"
                        }`}
                    >
                    {capitalizeFirstLetter(row.task_source)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && !isFilterOn && (
        <div className={`d-flex justify-content-between align-items-center mt-4 px-2 ${isPageScreen ? "flex-column gap-3" : ""}`}>
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center gap-2">
              <span style={{
                fontSize: isSmallScreen ? "12px" : "14px",
                whiteSpace: "nowrap"
              }}>
                Show
              </span>

              <select
                className="form-select form-select-sm"
                style={{
                  width: "60px",
                  fontSize: isSmallScreen ? "12px" : "14px",
                  padding: isSmallScreen ? "2px 4px" : "2px 6px"
                }}
                name="perpage"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>

              <span
                style={{
                  fontSize: isSmallScreen ? "12px" : "14px",
                  fontWeight: 500,
                  color: "#495057",
                }}
              >
                per page
              </span>
            </div>
          </div>

          <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
            <div className="d-flex align-items-center">
              <Pagination
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  "& .MuiPaginationItem-root": {
                    fontSize: isSmallScreen ? "11px" : "14px",
                    minWidth: isSmallScreen ? "28px" : "36px",
                    height: isSmallScreen ? "28px" : "36px",
                    margin: isSmallScreen ? "0 1px" : "0 2px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    color: "#374151",
                    "&:hover": {
                      backgroundColor: "#f9fafb",
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#6366f1",
                      color: "#ffffff",
                      borderColor: "#6366f1",
                      "&:hover": {
                        backgroundColor: "#4f46e5",
                        borderColor: "#4f46e5",
                      },
                    },
                  },
                }}
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                renderItem={(item) => {
                  if (item.type === "page") {
                    const isNearCurrent =
                      Math.abs(item.page - currentPage) <= 1 ||
                      item.page === 1 ||
                      item.page === totalPages;
                    return isNearCurrent ? <PaginationItem {...item} /> : null;
                  }
                  return <PaginationItem {...item} />;
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnAssignedTickets;